// Page Visibility API helpers (`document.hidden` / `visibilitychange`), free of
// React so they can drive a long-lived loop (the SSE reader) without a render.

/** Whether the document is currently hidden (background tab, minimized window). */
export function isDocumentHidden(): boolean {
  return typeof document !== 'undefined' && document.hidden === true;
}

/**
 * Resolve once the document is visible — immediately if it already is — or
 * when `signal` aborts, whichever comes first. The listener is removed on both
 * exits so a resolved wait never leaves one behind.
 */
export function waitForVisible(signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted || !isDocumentHidden()) {
      resolve();
      return;
    }

    const done = (): void => {
      document.removeEventListener('visibilitychange', onChange);
      signal.removeEventListener('abort', done);
      resolve();
    };
    const onChange = (): void => {
      if (!isDocumentHidden()) done();
    };
    document.addEventListener('visibilitychange', onChange);
    signal.addEventListener('abort', done, { once: true });
  });
}

/**
 * Call `callback` once the document has stayed hidden for `ms` in a row. The
 * timer is armed when the document becomes hidden — or right away if it already
 * is — and cleared as soon as it becomes visible again, so a short switch to
 * another tab never fires. Fires at most once per hidden period: the timer is
 * re-armed on the next hide.
 *
 * Returns an unsubscribe function that clears the timer and the listener.
 */
export function onHiddenFor(ms: number, callback: () => void): () => void {
  if (typeof document === 'undefined') return () => undefined;

  let timer: ReturnType<typeof setTimeout> | null = null;

  const arm = (): void => {
    if (timer !== null) return;
    timer = setTimeout(() => {
      timer = null;
      callback();
    }, ms);
  };
  const disarm = (): void => {
    if (timer === null) return;
    clearTimeout(timer);
    timer = null;
  };
  const onChange = (): void => {
    if (isDocumentHidden()) arm();
    else disarm();
  };

  document.addEventListener('visibilitychange', onChange);
  if (isDocumentHidden()) arm();

  return () => {
    disarm();
    document.removeEventListener('visibilitychange', onChange);
  };
}
